import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  RestaurantMenu as RecipeIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { InventoryItem, Warehouse, InventoryCategory } from '../types/inventory.types';
import { accountingApi } from '../../../../config/apiClient';
import InventoryProductSelectionModal from './InventoryProductSelectionModal';

// Types
interface ProductRecipe {
  id: string;
  companyId: string;
  inventoryItemId: string;
  inventoryItemName?: string;
  recipeName: string;
  recipeCode?: string;
  description?: string;
  outputQuantity: number;
  outputUnit: string;
  allowNegativeStock: boolean;
  isFlatteningRequired: boolean;
  lastFlattenedAt?: string;
  flattenedVersion: number;
  isActive: boolean;
  createdAt: string;
  ingredients?: RecipeIngredient[];
  // Backend her istekte stok kartlarındaki güncel alış fiyatlarından hesaplar
  totalCost?: number;
  unitCost?: number;
}

interface RecipeIngredient {
  id: string;
  companyId: string;
  recipeId: string;
  ingredientItemId: string;
  ingredientName?: string;
  ingredientBarcode?: string;
  ingredientStockCode?: string;
  quantity: number;
  unit: string;
  wastagePercent: number;
  canBeNegative: boolean;
  hasSubRecipe: boolean;
  subRecipeId?: string;
  sortOrder: number;
  notes?: string;
  isActive: boolean;
  // Etkin birim maliyet: stok kartından canlı okunur, alt reçeteli malzemede
  // alt reçetenin birim maliyeti gelir
  purchasePrice?: number;
  purchaseCurrency?: string;
  lineCost?: number;
  isCostFromSubRecipe?: boolean;
}

interface RecipeModalProps {
  open: boolean;
  onClose: () => void;
  item: InventoryItem | null;
  inventoryItems: InventoryItem[];
  companyId: string;
  onSuccess?: () => void;
  // ✅ Ürün seçim modalı filtreleri için (depo/kategori)
  warehouses?: Warehouse[];
  categories?: InventoryCategory[];
}

// ✅ Ürün seçim modalından seçilen, miktar/fire/not girilmeyi bekleyen malzeme satırı
interface PendingIngredient {
  item: InventoryItem;
  quantity: string;
  wastagePercent: string;
  notes: string;
}

interface EditingIngredient {
  id: string;
  quantity: number;
  wastagePercent: number;
  notes?: string;
}

// Maliyeti backend hesaplar: birim fiyat stok kartından canlı okunur, malzemenin kendi
// reçetesi varsa alt reçetenin birim maliyeti kullanılır. Alan gelmezse yerel hesaba düşülür.
const getIngredientCost = (ingredient: RecipeIngredient): number =>
  ingredient.lineCost ??
  ingredient.quantity * (1 + (ingredient.wastagePercent || 0) / 100) * (ingredient.purchasePrice || 0);

const getRecipeTotalCost = (recipe: ProductRecipe): number =>
  recipe.totalCost ?? (recipe.ingredients || []).reduce((total, ing) => total + getIngredientCost(ing), 0);

// ✅ Türkçe locale destekli decimal parse fonksiyonu
// Virgül (,) ve nokta (.) her ikisini de destekler
// Sonucu 4 ondalık haneye yuvarlar
const parseDecimalTR = (value: string | number): number => {
  if (typeof value === 'number') {
    return Math.round(value * 10000) / 10000; // 4 haneye yuvarla
  }
  if (!value || typeof value !== 'string') return 0;
  
  // Boşlukları temizle
  let cleaned = value.trim();
  
  // Türkçe format: 1.234,56 → 1234.56
  // İngilizce format: 1,234.56 → 1234.56
  
  // Hem nokta hem virgül varsa, hangisi son ise o ondalık ayıracı
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  
  if (lastComma > lastDot) {
    // Türkçe format: virgül ondalık ayıracı
    cleaned = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    // İngilizce format: nokta ondalık ayıracı
    cleaned = cleaned.replace(/,/g, '');
  } else if (lastComma !== -1) {
    // Sadece virgül var
    cleaned = cleaned.replace(',', '.');
  }
  // Sadece nokta varsa zaten doğru format
  
  const result = parseFloat(cleaned);
  if (isNaN(result)) return 0;
  
  // 4 ondalık haneye yuvarla (floating point hatalarını önler)
  return Math.round(result * 10000) / 10000;
};

export const RecipeModal: React.FC<RecipeModalProps> = ({
  open,
  onClose,
  item,
  inventoryItems,
  companyId,
  onSuccess,
  warehouses = [],
  categories = []
}) => {
  const { i18n } = useTranslation();
  // startsWith: "tr-TR" gibi bölgeli kodlarda da doğru çalışsın (bkz. src/i18n/index.ts)
  const isTr = !(i18n.language || 'tr').toLowerCase().startsWith('en');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  // State
  const [recipe, setRecipe] = useState<ProductRecipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showInfoBox, setShowInfoBox] = useState(false);

  // Reçete bilgileri
  const [recipeName, setRecipeName] = useState('');
  const [recipeCode, setRecipeCode] = useState('');
  const [description, setDescription] = useState('');

  // ✅ YENİ: Malzeme ekleme — Autocomplete yerine gelişmiş ürün seçim modalı.
  // Modaldan seçilen ürünler pendingIngredients listesine düşer; kullanıcı
  // her satır için miktar/fire/not girer ve toplu ekler.
  const [isProductSelectOpen, setIsProductSelectOpen] = useState(false);
  const [pendingIngredients, setPendingIngredients] = useState<PendingIngredient[]>([]);
  const [addingIngredient, setAddingIngredient] = useState(false);

  // Düzenleme
  const [editingIngredient, setEditingIngredient] = useState<EditingIngredient | null>(null);

  // Döngüsel bağımlılık kontrolü
  const [circularError, setCircularError] = useState<string | null>(null);

  // ✅ Silme onay dialog'ları için state'ler
  const [deleteIngredientDialogOpen, setDeleteIngredientDialogOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState<string | null>(null);
  const [deleteRecipeDialogOpen, setDeleteRecipeDialogOpen] = useState(false);

  // Modal açıldığında
  useEffect(() => {
    if (open && item) {
      loadRecipe();
      setPendingIngredients([]);
      setRecipeName(`${item.name} ${tt('Reçetesi', 'Recipe')}`);
      setRecipeCode('');
      setDescription('');
      setError(null);
      setSuccessMessage(null);
      setCircularError(null);
      setShowInfoBox(false);
    }
  }, [open, item]);

  // Reçete yükle
  const loadRecipe = async () => {
    if (!item || !companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // ✅ Önce ana ürünün güncel bilgisini çek
      let freshItem = null;
      try {
        const itemResponse = await accountingApi.get(
          `/api/Inventory/items/${item.id}`
        );
        freshItem = itemResponse.data;
      } catch {}
      const currentCanBeNegative = freshItem?.canBeNegative ?? item.canBeNegative;

      let response;
      try {
        response = await accountingApi.get(
          `/api/ProductRecipe/by-item/${item.id}`
        );
      } catch (err: any) {
        if (err.response?.status === 404) {
          setRecipe(null);
          return;
        }
        throw new Error(tt('Reçete yüklenirken hata oluştu', 'An error occurred while loading recipe'));
      }
      
      let data: ProductRecipe = response.data;
      
      // ✅ Reçetedeki allowNegativeStock ile ana ürünün güncel canBeNegative değeri farklıysa güncelle
      if (data.allowNegativeStock !== currentCanBeNegative) {
        console.log(`🔄 [REÇETE] Ana ürün canBeNegative değişmiş, reçete güncelleniyor: ${data.allowNegativeStock} → ${currentCanBeNegative}`);
        
        try {
          await accountingApi.post(
            `/api/ProductRecipe/${data.id}/update`,
            {
              ...data,
              allowNegativeStock: currentCanBeNegative
            }
          );
          data = { ...data, allowNegativeStock: currentCanBeNegative };
        } catch {}
      }
      
      setRecipe(data);
      setRecipeName(data.recipeName);
      setRecipeCode(data.recipeCode || '');
      setDescription(data.description || '');
    } catch (err: any) {
      console.error('Reçete yükleme hatası:', err);
      setRecipe(null);
    } finally {
      setLoading(false);
    }
  };

  // Reçete oluştur
  const createRecipe = async () => {
    if (!item || !companyId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await accountingApi.post(`/api/ProductRecipe`, {
        companyId,
        inventoryItemId: item.id,
        recipeName: recipeName || `${item.name} ${tt('Reçetesi', 'Recipe')}`,
        recipeCode: recipeCode || undefined,
        description: description || undefined,
        allowNegativeStock: item.canBeNegative
      });
      
      const data: ProductRecipe = response.data;
      setRecipe(data);
      setSuccessMessage(tt('Reçete başarıyla oluşturuldu!', 'Recipe created successfully!'));
      onSuccess?.();
    } catch (err: any) {
      console.error('❌ [RECIPE] Reçete oluşturma hatası:', err.response?.status, err.response?.data);
      const apiError = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(apiError);
    } finally {
      setSaving(false);
    }
  };

  // Döngüsel bağımlılık kontrolü
  const checkCircularDependency = async (ingredientItemId: string): Promise<boolean> => {
    if (!recipe || !companyId) return false;
    
    try {
      const response = await accountingApi.post(`/api/ProductRecipe/check-circular`, {
        companyId,
        recipeId: recipe.id,
        ingredientItemId
      });
      
      const data = response.data;
      if (data.hasCircularDependency) {
        setCircularError(data.message || tt('Döngüsel bağımlılık tespit edildi!', 'Circular dependency detected!'));
        return true;
      }
      
      setCircularError(null);
      return false;
    } catch {
      return false;
    }
  };

  // ✅ Ürün seçim modalından seçilenleri bekleyen listeye ekle (+ döngüsel kontrol)
  const handleProductsSelected = async (products: InventoryItem[]) => {
    setCircularError(null);
    const existing = new Set(pendingIngredients.map(p => p.item.id));
    const newRows: PendingIngredient[] = products
      .filter(p => !existing.has(p.id))
      .map(p => ({ item: p, quantity: '', wastagePercent: '0', notes: '' }));
    if (newRows.length === 0) return;

    setPendingIngredients(prev => [...prev, ...newRows]);

    // Döngüsel bağımlılık kontrolü (seçim anında erken uyarı)
    if (recipe) {
      for (const row of newRows) {
        const hasCircular = await checkCircularDependency(row.item.id);
        if (hasCircular) {
          // Döngü yaratan ürünü listeden çıkar
          setPendingIngredients(prev => prev.filter(p => p.item.id !== row.item.id));
        }
      }
    }
  };

  // ✅ API'den güncel ürün bilgisini çek
  const fetchItemFromAPI = async (itemId: string): Promise<InventoryItem | null> => {
    try {
      const response = await accountingApi.get(
        `/api/Inventory/items/${itemId}`
      );
      return response.data;
    } catch (error) {
      console.error('Ürün bilgisi çekilemedi:', error);
      return null;
    }
  };

  // ✅ Bekleyen malzemeleri toplu ekle (her satır için miktar zorunlu)
  const handleAddPendingIngredients = async () => {
    if (!recipe || pendingIngredients.length === 0) return;

    const missingQuantity = pendingIngredients.find(p => parseDecimalTR(p.quantity) <= 0);
    if (missingQuantity) {
      setError(tt(
        `'${missingQuantity.item.name}' için geçerli bir miktar girin.`,
        `Enter a valid quantity for '${missingQuantity.item.name}'.`
      ));
      return;
    }

    setAddingIngredient(true);
    setError(null);

    const failed: string[] = [];
    let sortOrder = (recipe.ingredients?.length || 0) + 1;

    for (const row of pendingIngredients) {
      let selectedItem = { ...row.item };

      // ✅ API'den güncel canBeNegative değerini çek
      const freshItem = await fetchItemFromAPI(selectedItem.id);
      if (freshItem) {
        selectedItem = { ...selectedItem, canBeNegative: freshItem.canBeNegative };
      }

      // CanBeNegative tutarlılık kontrolü (güncel veri ile)
      if (recipe.allowNegativeStock !== selectedItem.canBeNegative) {
        failed.push(
          recipe.allowNegativeStock
            ? `'${selectedItem.name}' ürünü için 'Stok eksiye düşebilir' ayarı kapalı. Reçete ayarı ile uyuşmuyor!`
            : `'${selectedItem.name}' ürünü için 'Stok eksiye düşebilir' ayarı açık. Reçete ayarı ile uyuşmuyor!`
        );
        continue;
      }

      try {
        await accountingApi.post(
          `/api/ProductRecipe/${recipe.id}/ingredients`,
          {
            companyId,
            recipeId: recipe.id,
            ingredientItemId: selectedItem.id,
            quantity: parseDecimalTR(row.quantity),
            unit: selectedItem.unit,
            wastagePercent: parseDecimalTR(row.wastagePercent),
            notes: row.notes || undefined,
            sortOrder: sortOrder++
            // Alış fiyatı gönderilmiyor: backend stok kartından okur, maliyet de her
            // istekte kartın güncel fiyatından hesaplanır.
          }
        );
        // Başarılı eklenen satırı listeden düş
        setPendingIngredients(prev => prev.filter(p => p.item.id !== row.item.id));
      } catch (err: any) {
        failed.push(`'${selectedItem.name}': ${err.response?.data?.message || err.message}`);
      }
    }

    // Reçeteyi yeniden yükle
    await loadRecipe();

    if (failed.length > 0) {
      setError(failed.join(' • '));
    } else {
      setSuccessMessage(tt('Malzemeler eklendi!', 'Ingredients added!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    }
    setAddingIngredient(false);
  };

  // Malzeme güncelle
  const handleUpdateIngredient = async () => {
    if (!editingIngredient || !companyId) return;
    
    setSaving(true);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductRecipe/ingredients/${editingIngredient.id}/update`,
        {
          quantity: editingIngredient.quantity,
          wastagePercent: editingIngredient.wastagePercent,
          notes: editingIngredient.notes
        }
      );
      
      await loadRecipe();
      setEditingIngredient(null);
      setSuccessMessage(tt('Malzeme güncellendi!', 'Ingredient updated!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Malzeme silme dialog'unu aç
  const openDeleteIngredientDialog = (ingredientId: string) => {
    setIngredientToDelete(ingredientId);
    setDeleteIngredientDialogOpen(true);
  };

  // Malzeme sil (dialog onayından sonra)
  const handleDeleteIngredient = async () => {
    if (!companyId || !ingredientToDelete) return;
    
    setDeleteIngredientDialogOpen(false);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductRecipe/ingredients/${ingredientToDelete}/delete`
      );
      
      await loadRecipe();
      setSuccessMessage(tt('Malzeme silindi!', 'Ingredient deleted!'));
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIngredientToDelete(null);
    }
  };

  // Reçete silme dialog'unu aç
  const openDeleteRecipeDialog = () => {
    setDeleteRecipeDialogOpen(true);
  };

  // Reçete sil (dialog onayından sonra)
  const handleDeleteRecipe = async () => {
    if (!recipe || !companyId) return;
    
    setDeleteRecipeDialogOpen(false);
    setSaving(true);
    setError(null);
    
    try {
      await accountingApi.post(
        `/api/ProductRecipe/${recipe.id}/delete`
      );
      
      setRecipe(null);
      setSuccessMessage(tt('Reçete silindi!', 'Recipe deleted!'));
      onSuccess?.();
    } catch (err: any) {
      console.error('❌ [RECIPE] Reçete silme hatası:', err.response?.status, err.response?.data);
      const apiError = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(apiError);
    } finally {
      setSaving(false);
    }
  };

  // Fire dahil toplam miktar hesapla
  const calculateTotalWithWastage = (quantity: number, wastagePercent: number): number => {
    return quantity * (1 + wastagePercent / 100);
  };

  // ✅ Miktar formatlama - gereksiz sıfırları kaldır, Türkçe format
  const formatQuantity = (value: number): string => {
    // Tam sayıysa ondalık gösterme
    if (Number.isInteger(value)) {
      return value.toString();
    }
    // Ondalıklı ise gereksiz sıfırları kaldır (max 3 basamak)
    const formatted = value.toFixed(3);
    return formatted.replace(/\.?0+$/, '').replace('.', ',');
  };

  // ✅ Yazdırma fonksiyonu
  const handlePrint = () => {
    if (!recipe || !recipe.ingredients?.length) return;

    const totalCost = getRecipeTotalCost(recipe);

    // Para formatla
    const formatCurrency = (value: number) => {
      return value.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';
    };

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title></title>
        <style>
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
          @media print {
            html, body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.4;
            color: #000;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #333;
          }
          .header h1 {
            font-size: 18pt;
            font-weight: bold;
            text-transform: uppercase;
          }
          .product-info {
            margin-bottom: 15px;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 4px;
          }
          .product-info p {
            margin: 3px 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            border: 1px solid #333;
            padding: 6px 8px;
            text-align: left;
            font-size: 10pt;
          }
          th {
            background: #333;
            color: #fff;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background: #f9f9f9;
          }
          .text-right {
            text-align: right;
          }
          .text-center {
            text-align: center;
          }
          .total-section {
            margin-top: 15px;
            padding: 12px;
            background: #e8f5e9;
            border: 2px solid #4caf50;
            border-radius: 4px;
            text-align: right;
          }
          .total-section .label {
            font-size: 11pt;
            color: #333;
          }
          .total-section .value {
            font-size: 16pt;
            font-weight: bold;
            color: #2e7d32;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${(item?.name || recipe.recipeName || '').toUpperCase()} ${tt('REÇETESİ', 'RECIPE')}</h1>
        </div>
        
        <div class="product-info">
          <p><strong>${tt('Ürün', 'Product')}:</strong> ${item?.name || '-'}</p>
          <p><strong>${tt('Stok Kodu', 'Stock Code')}:</strong> ${item?.stockCode || '-'}</p>
          ${recipe.description ? `<p><strong>${tt('Açıklama', 'Description')}:</strong> ${recipe.description}</p>` : ''}
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 4%">#</th>
              <th style="width: 28%">${tt('Malzeme', 'Ingredient')}</th>
              <th style="width: 10%">${tt('Birim', 'Unit')}</th>
              <th class="text-right" style="width: 12%">${tt('Miktar', 'Quantity')}</th>
              <th class="text-right" style="width: 10%">${tt('Fire %', 'Waste %')}</th>
              <th class="text-right" style="width: 12%">${tt('Fire Dahil', 'Including Waste')}</th>
              <th class="text-right" style="width: 12%">${tt('Birim Fiyat', 'Unit Price')}</th>
              <th class="text-right" style="width: 12%">${tt('Maliyet', 'Cost')}</th>
            </tr>
          </thead>
          <tbody>
            ${recipe.ingredients.map((ing, idx) => {
              const qtyWithWastage = ing.quantity * (1 + (ing.wastagePercent || 0) / 100);
              const cost = getIngredientCost(ing);
              return `
              <tr>
                <td class="text-center">${idx + 1}</td>
                <td>${ing.ingredientName || '-'}</td>
                <td>${ing.unit || '-'}</td>
                <td class="text-right">${formatQuantity(ing.quantity)}</td>
                <td class="text-right">${formatQuantity(ing.wastagePercent)}%</td>
                <td class="text-right">${formatQuantity(qtyWithWastage)}</td>
                <td class="text-right">${ing.purchasePrice ? formatCurrency(ing.purchasePrice) : '-'}</td>
                <td class="text-right">${cost > 0 ? formatCurrency(cost) : '-'}</td>
              </tr>
            `}).join('')}
          </tbody>
        </table>

        <div class="total-section">
          <span class="label">${tt('Toplam Üretim Maliyeti', 'Total Production Cost')} (${recipe.ingredients.length} ${tt('malzeme, fire dahil', 'ingredients, including waste')}):</span>
          <br/>
          <span class="value">${formatCurrency(totalCost)}</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={recipe ? 'lg' : 'sm'}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: recipe ? '1200px' : undefined // Özel genişlik
        }
      }}
      PaperProps={{
        sx: { minHeight: recipe ? '50vh' : 'auto' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RecipeIcon color="warning" />
          <Typography variant="h6">
            {tt('Basit Üretim Reçetesi', 'Simple Production Recipe')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={tt('Basit üretim bilgisi', 'Simple production information')}>
            <IconButton
              size="small"
              onClick={() => setShowInfoBox(prev => !prev)}
              sx={{
                color: showInfoBox ? 'warning.main' : 'text.secondary',
                border: '1px solid',
                borderColor: showInfoBox ? 'warning.main' : 'divider',
                borderRadius: 1
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        {showInfoBox && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {tt('Basit Üretim Reçetesi Hakkında', 'About Simple Production Recipe')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {tt(
                'Bu modal ile bir ürün satıldığında stoktan düşecek malzemeleri ve miktarlarını tanımlarsınız.',
                'In this modal, you define which ingredients and quantities are deducted from stock when the product is sold.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• "Malzeme Ekle" ile reçeteye ürün ekleyin; miktar, fire oranı ve not bilgilerini girin.',
                '• Use "Add Ingredient" to add items to the recipe; enter quantity, waste rate, and optional notes.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• Alış fiyatı ve üretim maliyeti alanları, reçetenin toplam maliyetini görmenizi ve maliyet kontrolü yapmanızı kolaylaştırır.',
                '• Purchase price and production cost fields help you see total recipe cost and control profitability.'
              )}
            </Typography>
            <Typography variant="body2">
              {tt(
                '• Reçete kaydı aktif olduktan sonra malzeme satırlarını güncelleyebilir, silebilir ve gerekirse reçeteyi yazdırabilirsiniz.',
                '• After creating the recipe, you can edit/delete ingredient rows and print the recipe when needed.'
              )}
            </Typography>
          </Alert>
        )}

        {/* Ürün Bilgisi - Kompakt */}
        {item && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {tt('Ürün', 'Product')}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                  {item.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={item.stockCode} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                {item.barcode && <Chip label={item.barcode} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />}
                <Chip 
                  label={item.canBeNegative ? tt('Eksiye Düşebilir', 'Can Go Negative') : tt('Eksiye Düşemez', 'Cannot Go Negative')}
                  size="small" 
                  color={item.canBeNegative ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Hata ve Başarı Mesajları */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {circularError && <Alert severity="warning" sx={{ mb: 2 }}>{circularError}</Alert>}
        
        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Reçete Yok - Oluştur */}
        {!loading && !recipe && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <RecipeIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {tt('Henüz reçete tanımlanmamış', 'No recipe defined yet')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {tt('Satışta hangi malzemelerin stoktan düşeceğini belirleyin', 'Select which ingredients will be deducted from stock during sale')}
            </Typography>
            
            <Box sx={{ maxWidth: 350, mx: 'auto' }}>
              <TextField
                fullWidth
                size="small"
                label={tt('Reçete Adı', 'Recipe Name')}
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={tt('Reçete Kodu (Opsiyonel)', 'Recipe Code (Optional)')}
                value={recipeCode}
                onChange={(e) => setRecipeCode(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                size="small"
                label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={createRecipe}
                disabled={saving}
              >
                {saving ? tt('Oluşturuluyor...', 'Creating...') : tt('Reçete Oluştur', 'Create Recipe')}
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Reçete Var - Malzeme Listesi */}
        {!loading && recipe && (
          <>
            {/* Reçete Bilgileri */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {tt('Reçete', 'Recipe')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {recipe.recipeName}
              </Typography>
              {recipe.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {recipe.description}
                </Typography>
              )}
            </Box>

            {/* Malzeme Ekle Butonu — gelişmiş ürün seçim modalını açar */}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setIsProductSelectOpen(true)}
              sx={{ mb: 2 }}
            >
              {tt('Malzeme Ekle', 'Add Ingredient')}
            </Button>

            {/* Seçilen malzemeler — miktar/fire/not girilip toplu eklenir */}
            {pendingIngredients.length > 0 && (
              <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {tt('Seçilen Malzemeler', 'Selected Ingredients')} ({pendingIngredients.length})
                </Typography>
                {pendingIngredients.map((row, index) => (
                  <Box key={row.item.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 220, flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">{row.item.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.item.stockCode} | {tt('Birim', 'Unit')}: {row.item.unit} |
                        <span style={{ fontWeight: 500, color: '#1976d2' }}>
                          {' '}{tt('Alış', 'Purchase')}: {row.item.purchasePrice ? `${row.item.purchasePrice.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺` : '-'}
                        </span>
                      </Typography>
                    </Box>

                    <TextField
                      label={tt('Miktar', 'Quantity')}
                      type="number"
                      size="small"
                      value={row.quantity}
                      onChange={(e) => {
                        const next = [...pendingIngredients];
                        next[index] = { ...row, quantity: e.target.value };
                        setPendingIngredients(next);
                      }}
                      sx={{ width: 130, flexShrink: 0 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{row.item.unit}</InputAdornment>
                        )
                      }}
                    />

                    <TextField
                      label={tt('Fire %', 'Waste %')}
                      type="number"
                      size="small"
                      value={row.wastagePercent}
                      onChange={(e) => {
                        const next = [...pendingIngredients];
                        next[index] = { ...row, wastagePercent: e.target.value };
                        setPendingIngredients(next);
                      }}
                      sx={{ width: 90, flexShrink: 0 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                    />

                    {/* ✅ Üretim Maliyeti (inline - Fire dahil) */}
                    <Box sx={{
                      bgcolor: 'success.50',
                      border: '1px solid',
                      borderColor: 'success.200',
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.5,
                      minWidth: 120,
                      textAlign: 'center',
                      flexShrink: 0
                    }}>
                      <Typography variant="caption" color="text.secondary" display="block" fontSize={11}>
                        {tt('Üretim Maliyeti', 'Production Cost')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main" fontSize={13}>
                        {row.item.purchasePrice && parseDecimalTR(row.quantity) > 0
                          ? `${(parseDecimalTR(row.quantity) * (1 + parseDecimalTR(row.wastagePercent) / 100) * row.item.purchasePrice).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₺`
                          : '-'}
                      </Typography>
                    </Box>

                    <TextField
                      label={tt('Not', 'Note')}
                      size="small"
                      value={row.notes}
                      onChange={(e) => {
                        const next = [...pendingIngredients];
                        next[index] = { ...row, notes: e.target.value };
                        setPendingIngredients(next);
                      }}
                      sx={{ width: 120, flexShrink: 0 }}
                    />

                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setPendingIngredients(prev => prev.filter(p => p.item.id !== row.item.id))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={addingIngredient ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleAddPendingIngredients}
                    disabled={addingIngredient || pendingIngredients.length === 0}
                  >
                    {tt(`Hepsini Ekle (${pendingIngredients.length})`, `Add All (${pendingIngredients.length})`)}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setPendingIngredients([]);
                      setCircularError(null);
                    }}
                  >
                    {tt('İptal', 'Cancel')}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Malzeme Listesi */}
            {recipe.ingredients && recipe.ingredients.length > 0 ? (
              <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>{tt('Malzeme', 'Ingredient')}</TableCell>
                      <TableCell align="center">{tt('Miktar', 'Quantity')}</TableCell>
                      <TableCell align="center">{tt('Birim', 'Unit')}</TableCell>
                      <TableCell align="right">{tt('Alış Fiyatı', 'Purchase Price')}</TableCell>
                      <TableCell align="right">{tt('Üretim Maliyeti', 'Production Cost')}</TableCell>
                      <TableCell align="center">{tt('Fire %', 'Waste %')}</TableCell>
                      <TableCell align="center">{tt('Fire Dahil', 'Including Waste')}</TableCell>
                      <TableCell align="center">{tt('Stok Durumu', 'Stock Status')}</TableCell>
                      <TableCell align="center">{tt('İşlemler', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipe.ingredients.map((ingredient) => (
                      <TableRow key={ingredient.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {ingredient.ingredientName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {ingredient.ingredientStockCode}
                            </Typography>
                            {ingredient.hasSubRecipe && (
                              <Chip 
                                label={tt('Alt Reçete', 'Sub Recipe')}
                                size="small" 
                                color="warning" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {editingIngredient?.id === ingredient.id ? (
                            <TextField
                              type="number"
                              size="small"
                              value={editingIngredient.quantity}
                              onChange={(e) => setEditingIngredient({
                                ...editingIngredient,
                                quantity: parseDecimalTR(e.target.value)
                              })}
                              sx={{ width: 80 }}
                            />
                          ) : (
                            <Typography variant="body2">{ingredient.quantity}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{ingredient.unit}</Typography>
                        </TableCell>
                        {/* ✅ YENİ: Alış Fiyatı Sütunu */}
                        <TableCell align="right">
                          <Typography variant="body2">
                            {ingredient.purchasePrice != null 
                              ? `${ingredient.purchasePrice.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺/${ingredient.unit}`
                              : '-'}
                          </Typography>
                        </TableCell>
                        {/* ✅ Üretim Maliyeti Sütunu (Miktar × (1 + Fire%) × Birim maliyet) */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            {ingredient.purchasePrice != null && ingredient.quantity > 0
                              ? `${getIngredientCost(ingredient).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {editingIngredient?.id === ingredient.id ? (
                            <TextField
                              type="number"
                              size="small"
                              value={editingIngredient.wastagePercent}
                              onChange={(e) => setEditingIngredient({
                                ...editingIngredient,
                                wastagePercent: parseDecimalTR(e.target.value)
                              })}
                              sx={{ width: 60 }}
                            />
                          ) : (
                            <Typography variant="body2">
                              {ingredient.wastagePercent > 0 ? `${ingredient.wastagePercent}%` : '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium" color="warning.main">
                            {formatQuantity(calculateTotalWithWastage(ingredient.quantity, ingredient.wastagePercent))}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={ingredient.canBeNegative ? tt('Stok eksiye düşebilir', 'Stock can go negative') : tt('Stok eksiye düşemez', 'Stock cannot go negative')}>
                            {ingredient.canBeNegative ? (
                              <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                              <WarningIcon fontSize="small" color="error" />
                            )}
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          {editingIngredient?.id === ingredient.id ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={handleUpdateIngredient}
                                disabled={saving}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setEditingIngredient(null)}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setEditingIngredient({
                                  id: ingredient.id,
                                  quantity: ingredient.quantity,
                                  wastagePercent: ingredient.wastagePercent,
                                  notes: ingredient.notes
                                })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => openDeleteIngredientDialog(ingredient.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* ✅ YENİ: Toplam Üretim Maliyeti Özeti (Fire dahil) */}
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                bgcolor: 'success.50', 
                border: '1px solid',
                borderColor: 'success.200',
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {tt('Toplam Üretim Maliyeti', 'Total Production Cost')} ({recipe.ingredients.length} {tt('malzeme, fire dahil', 'ingredients, including waste')})
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {getRecipeTotalCost(recipe).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                </Typography>
              </Box>
              </>
            ) : (
              <Alert severity="info">
                {tt('Henüz malzeme eklenmemiş. "Malzeme Ekle" butonunu kullanarak malzeme ekleyin.', 'No ingredients added yet. Use the "Add Ingredient" button to add ingredients.')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        {recipe && (
          <Button
            color="error"
            onClick={openDeleteRecipeDialog}
            disabled={saving}
          >
            {tt('Reçeteyi Sil', 'Delete Recipe')}
          </Button>
        )}
        {recipe && recipe.ingredients && recipe.ingredients.length > 0 && (
          <Button
            color="primary"
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            {tt('Yazdır', 'Print')}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="inherit">
          {tt('Kapat', 'Close')}
        </Button>
      </DialogActions>

      {/* ✅ Malzeme Silme Onay Dialog'u */}
      <Dialog
        open={deleteIngredientDialogOpen}
        onClose={() => setDeleteIngredientDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          {tt('Malzeme Silme Onayı', 'Ingredient Delete Confirmation')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {tt('Bu malzemeyi reçeteden silmek istediğinize emin misiniz?', 'Are you sure you want to delete this ingredient from the recipe?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteIngredientDialogOpen(false)} 
            color="inherit"
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteIngredient} 
            color="error" 
            variant="contained"
          >
            {tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Reçete Silme Onay Dialog'u */}
      <Dialog
        open={deleteRecipeDialogOpen}
        onClose={() => setDeleteRecipeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon color="error" />
          {tt('Reçete Silme Onayı', 'Recipe Delete Confirmation')}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {tt(
              `"${recipe?.recipeName || item?.name}" reçetesini tamamen silmek istediğinize emin misiniz?`,
              `Are you sure you want to completely delete the "${recipe?.recipeName || item?.name}" recipe?`
            )}
          </Typography>
          <Alert severity="error" sx={{ mt: 2 }}>
            {tt('Bu işlem geri alınamaz! Tüm malzemeler ve ayarlar silinecektir.', 'This action cannot be undone! All ingredients and settings will be deleted.')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteRecipeDialogOpen(false)} 
            color="inherit"
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            onClick={handleDeleteRecipe} 
            color="error" 
            variant="contained"
            disabled={saving}
          >
            {tt('Reçeteyi Sil', 'Delete Recipe')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Gelişmiş Ürün Seçim Modalı (Autocomplete yerine) */}
      <InventoryProductSelectionModal
        open={isProductSelectOpen}
        onClose={() => setIsProductSelectOpen(false)}
        title={tt('Reçeteye Malzeme Seç', 'Select Ingredients for Recipe')}
        warehouses={warehouses}
        categories={categories}
        excludeItemIds={[
          ...(item ? [item.id] : []),
          ...(recipe?.ingredients?.map(i => i.ingredientItemId) || []),
          ...pendingIngredients.map(p => p.item.id),
        ]}
        onSelectProducts={handleProductsSelected}
      />
    </Dialog>
  );
};
